import { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";

interface SettingsData {
  aff_amount: string;
  invitee_amount: string;
  telegram_link: string;
  whatsapp_no: string;

  min_deposit: string;
  min_valid_bets: string;
  time_window: string;

  withdrawal_fee: string; // NEW
  paytypecode: string; // NEW
  deposit_turnover: string;
  referral_deposit_bonus_percent: string;
  marquee_msg: string; // ✅ NEW
  banners: string[]; // ✅ NEW

  // Footer settings
  footer_tagline: string;
  footer_copyright: string;
  footer_about_us: string;
  footer_sponsors: any[];
  footer_ambassadors: any[];
  footer_social_links: Record<string, string>;
  footer_payment_methods: any[];
  footer_responsible_gaming: string[];
  footer_gaming_license: { img: string; link: string } | string;
}

const Settings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [settings, setSettings] = useState<SettingsData>({
    aff_amount: "",
    invitee_amount: "",
    telegram_link: "",
    whatsapp_no: "",

    min_deposit: "",
    min_valid_bets: "",
    time_window: "",

    withdrawal_fee: "",
    paytypecode: "",
    // payout_code: "",
    deposit_turnover: "",
    referral_deposit_bonus_percent: "",
    marquee_msg: "", // ✅ NEW
    banners: [], // ✅ NEW

    footer_tagline: "Win Like A King",
    footer_copyright: "© 2025 Jeetoo Copyrights. All Rights Reserved",
    footer_about_us: "",
    footer_sponsors: [],
    footer_ambassadors: [],
    footer_social_links: {},
    footer_payment_methods: [],
    footer_responsible_gaming: [],
    footer_gaming_license: { img: "", link: "" },
  });
  const [loading, setLoading] = useState(false);

  /* ================= FETCH SETTINGS ================= */
  const fetchSettings = async () => {
    try {
      const res = await api.get("/getPaymentSetting");
      setSettings({
        aff_amount: res.data?.aff_amount || "",
        invitee_amount: res.data?.invitee_amount || "",
        telegram_link: res.data?.telegram_link || "",
        whatsapp_no: res.data?.whatsapp_no || "",

        min_deposit: res.data?.min_deposit || "",
        min_valid_bets: res.data?.min_valid_bets || "",
        time_window: res.data?.time_window || "",

        withdrawal_fee: res.data?.withdrawal_fee || "",
        paytypecode: res.data?.paytypecode || "",
        deposit_turnover: res.data?.deposit_turnover || "",
        referral_deposit_bonus_percent: res.data?.referral_deposit_bonus_percent || "",
        marquee_msg: res.data?.marquee_msg || "", // ✅ NEW
        banners: (() => {
          if (!res.data?.banners) return [];
          try {
            return typeof res.data.banners === 'string' ? JSON.parse(res.data.banners) : res.data.banners;
          } catch (e) {
            return [];
          }
        })(), // ✅ NEW
        footer_tagline: res.data?.footer_tagline || "Win Like A King",
        footer_copyright: res.data?.footer_copyright || "© 2025 Jeetoo Copyrights. All Rights Reserved",
        footer_about_us: res.data?.footer_about_us || "",
        footer_sponsors: (() => {
          if (!res.data?.footer_sponsors) return [];
          try {
            return typeof res.data.footer_sponsors === 'string' ? JSON.parse(res.data.footer_sponsors) : res.data.footer_sponsors;
          } catch (e) {
            return [];
          }
        })(),
        footer_ambassadors: (() => {
          if (!res.data?.footer_ambassadors) return [];
          try {
            return typeof res.data.footer_ambassadors === 'string' ? JSON.parse(res.data.footer_ambassadors) : res.data.footer_ambassadors;
          } catch (e) {
            return [];
          }
        })(),
        footer_social_links: (() => {
          if (!res.data?.footer_social_links) return {};
          try {
            return typeof res.data.footer_social_links === 'string' ? JSON.parse(res.data.footer_social_links) : res.data.footer_social_links;
          } catch (e) {
            return {};
          }
        })(),
        footer_payment_methods: (() => {
          if (!res.data?.footer_payment_methods) return [];
          try {
            return typeof res.data.footer_payment_methods === 'string' ? JSON.parse(res.data.footer_payment_methods) : res.data.footer_payment_methods;
          } catch (e) {
            return [];
          }
        })(),
        footer_responsible_gaming: (() => {
          if (!res.data?.footer_responsible_gaming) return [];
          try {
            return typeof res.data.footer_responsible_gaming === 'string' ? JSON.parse(res.data.footer_responsible_gaming) : res.data.footer_responsible_gaming;
          } catch (e) {
            return [];
          }
        })(),
        footer_gaming_license: (() => {
          if (!res.data?.footer_gaming_license) return { img: "", link: "" };
          try {
            return typeof res.data.footer_gaming_license === 'string' ? JSON.parse(res.data.footer_gaming_license) : res.data.footer_gaming_license;
          } catch (e) {
            return { img: res.data.footer_gaming_license, link: "" };
          }
        })(),
      });
    } catch (err) {
      console.error("Failed to load settings", err);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= BANNER HANDLERS ================= */
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingBanner(true);
    const formData = new FormData();
    formData.append("banner", file);

    try {
      const res = await api.post("/upload-banner", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data?.filename) {
        setSettings((prev) => ({
          ...prev,
          banners: [...prev.banners, res.data.filename],
        }));
        Swal.fire({
          icon: "success",
          title: "Banner Uploaded",
          text: "Banner image uploaded successfully. Click Save Settings to persist.",
        });
      }
    } catch (err) {
      console.error("Banner upload failed", err);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Failed to upload banner image.",
      });
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  const handleBannerDelete = (indexToRemove: number) => {
    setSettings((prev) => ({
      ...prev,
      banners: prev.banners.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  /* ================= IMAGE URL RESOLVER ================= */
  const getImageUrl = (filename: string) => {
    if (!filename) return "";
    if (filename.startsWith("http://") || filename.startsWith("https://") || filename.startsWith("/assets/")) {
      return filename;
    }
    const apiBase = api.defaults.baseURL ? api.defaults.baseURL.replace(/\/admin\/?$/, "") : "https://api.jeetoo.bet";
    return `${apiBase}/uploads/${filename}`;
  };

  /* ================= FOOTER HANDLERS ================= */
  const handleSocialChange = (name: string, value: string) => {
    setSettings((prev) => ({
      ...prev,
      footer_social_links: {
        ...prev.footer_social_links,
        [name]: value,
      },
    }));
  };

  // Sponsors
  const [newSponsor, setNewSponsor] = useState({ name: "", role: "", year: "", img: "" });
  const [uploadingSponsorImg, setUploadingSponsorImg] = useState(false);

  const handleSponsorImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSponsorImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/upload-footer-img", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.filename) {
        setNewSponsor(prev => ({ ...prev, img: res.data.filename }));
        Swal.fire({ icon: "success", title: "Image Uploaded", text: "Sponsor image uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to upload image." });
    } finally {
      setUploadingSponsorImg(false);
    }
  };

  const addSponsor = () => {
    if (!newSponsor.name || !newSponsor.img) {
      Swal.fire({ icon: "warning", title: "Missing details", text: "Please enter name and upload an image." });
      return;
    }
    const item = {
      id: Date.now(),
      name: newSponsor.name,
      role: newSponsor.role,
      year: newSponsor.year,
      img: newSponsor.img
    };
    setSettings(prev => ({
      ...prev,
      footer_sponsors: [...prev.footer_sponsors, item]
    }));
    setNewSponsor({ name: "", role: "", year: "", img: "" });
  };

  const deleteSponsor = (id: number) => {
    setSettings(prev => ({
      ...prev,
      footer_sponsors: prev.footer_sponsors.filter(s => s.id !== id)
    }));
  };

  // Ambassadors
  const [newAmbassador, setNewAmbassador] = useState({ name: "", year: "", img: "" });
  const [uploadingAmbassadorImg, setUploadingAmbassadorImg] = useState(false);

  const handleAmbassadorImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAmbassadorImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/upload-footer-img", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.filename) {
        setNewAmbassador(prev => ({ ...prev, img: res.data.filename }));
        Swal.fire({ icon: "success", title: "Image Uploaded", text: "Ambassador image uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to upload image." });
    } finally {
      setUploadingAmbassadorImg(false);
    }
  };

  const addAmbassador = () => {
    if (!newAmbassador.name || !newAmbassador.img) {
      Swal.fire({ icon: "warning", title: "Missing details", text: "Please enter name and upload an image." });
      return;
    }
    const item = {
      id: Date.now(),
      name: newAmbassador.name,
      year: newAmbassador.year,
      img: newAmbassador.img
    };
    setSettings(prev => ({
      ...prev,
      footer_ambassadors: [...prev.footer_ambassadors, item]
    }));
    setNewAmbassador({ name: "", year: "", img: "" });
  };

  const deleteAmbassador = (id: number) => {
    setSettings(prev => ({
      ...prev,
      footer_ambassadors: prev.footer_ambassadors.filter(a => a.id !== id)
    }));
  };

  // Payment Methods
  const [newPaymentMethod, setNewPaymentMethod] = useState({ name: "", img: "" });
  const [uploadingPaymentImg, setUploadingPaymentImg] = useState(false);

  const handlePaymentImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPaymentImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/upload-footer-img", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.filename) {
        setNewPaymentMethod(prev => ({ ...prev, img: res.data.filename }));
        Swal.fire({ icon: "success", title: "Image Uploaded", text: "Payment method image uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to upload image." });
    } finally {
      setUploadingPaymentImg(false);
    }
  };

  const addPaymentMethod = () => {
    if (!newPaymentMethod.name || !newPaymentMethod.img) {
      Swal.fire({ icon: "warning", title: "Missing details", text: "Please enter name and upload an image." });
      return;
    }
    setSettings(prev => ({
      ...prev,
      footer_payment_methods: [...prev.footer_payment_methods, { name: newPaymentMethod.name, img: newPaymentMethod.img }]
    }));
    setNewPaymentMethod({ name: "", img: "" });
  };

  const deletePaymentMethod = (index: number) => {
    setSettings(prev => ({
      ...prev,
      footer_payment_methods: prev.footer_payment_methods.filter((_, idx) => idx !== index)
    }));
  };

  // Responsible Gaming
  const [uploadingResponsibleImg, setUploadingResponsibleImg] = useState(false);

  const handleResponsibleImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingResponsibleImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/upload-footer-img", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.filename) {
        setSettings(prev => ({
          ...prev,
          footer_responsible_gaming: [...prev.footer_responsible_gaming, res.data.filename]
        }));
        Swal.fire({ icon: "success", title: "Image Uploaded", text: "Responsible Gaming image uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to upload image." });
    } finally {
      setUploadingResponsibleImg(false);
    }
  };

  const deleteResponsibleGaming = (index: number) => {
    setSettings(prev => ({
      ...prev,
      footer_responsible_gaming: prev.footer_responsible_gaming.filter((_, idx) => idx !== index)
    }));
  };

  // Gaming License
  const [uploadingLicenseImg, setUploadingLicenseImg] = useState(false);

  const handleLicenseImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLicenseImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/upload-footer-img", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.filename) {
        setSettings(prev => {
          const currentLicense = typeof prev.footer_gaming_license === 'string'
            ? { img: prev.footer_gaming_license, link: "" }
            : prev.footer_gaming_license || { img: "", link: "" };
          return {
            ...prev,
            footer_gaming_license: {
              ...currentLicense,
              img: res.data.filename
            }
          };
        });
        Swal.fire({ icon: "success", title: "Image Uploaded", text: "Gaming License image uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Failed to upload image." });
    } finally {
      setUploadingLicenseImg(false);
    }
  };

  const handleLicenseLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSettings(prev => {
      const currentLicense = typeof prev.footer_gaming_license === 'string'
        ? { img: prev.footer_gaming_license, link: "" }
        : prev.footer_gaming_license || { img: "", link: "" };
      return {
        ...prev,
        footer_gaming_license: {
          ...currentLicense,
          link: val
        }
      };
    });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/updatePaymentSetting", settings);
      Swal.fire({
        icon: "success",
        title: "Settings Updated",
        text: "Your settings have been successfully updated.",
      });
      fetchSettings();
    } catch (err) {
      console.error("Update failed", err);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Failed to update settings.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 ">
      {/* ================= PAGE TITLE ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-sm text-gray-500">
          Manage affiliate commissions and support details
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* ================= AFFILIATE SETTINGS ================= */}
        {isAdmin && (
          <div className="bg-white border rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Affiliate Commission
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="hidden">
                <label className="text-sm text-gray-500 hidden">
                  Affiliate Commission Amount
                </label>
                <input
                  type="text"
                  name="aff_amount"
                  value={settings.aff_amount}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  placeholder="e.g. 10"
                />
              </div>

              <div className="hidden">
                <label className="text-sm text-gray-500">
                  Signup Bonus Amount
                </label>
                <input
                  type="text"
                  name="invitee_amount"
                  value={settings.invitee_amount}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  placeholder="e.g. 5"
                />
              </div>

              <div>
                <label className="text-sm text-gray-500">
                  Referral Deposit Bonus (%)
                </label>
                <input
                  type="text"
                  name="referral_deposit_bonus_percent"
                  value={settings.referral_deposit_bonus_percent}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  placeholder="e.g. 5"
                />
              </div>
            </div>
          </div>
        )}

        {/* ================= ADMIN CONTROLS ================= */}
        {isAdmin && (
          <>
            <div className="bg-white border rounded-2xl p-6 shadow-sm hidden">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Withdrawal Controls
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Withdrawal Fee */}
                <div>
                  <label className="text-sm text-gray-500">
                    Withdrawal Fee (%)
                  </label>
                  <input
                    type="number"
                    name="withdrawal_fee"
                    value={settings.withdrawal_fee}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    placeholder="e.g. 5"
                  />
                </div>

                {/* Paytypecode Dropdown */}
                <div className="hidden">
                  <label className="text-sm text-gray-500">FZPay Paytypecode</label>
                  <select
                    name="paytypecode"
                    value={settings.paytypecode}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        paytypecode: e.target.value,
                      }))
                    }
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                  >
                    <option value="">Select</option>
                    <option value="15001">15001</option>
                    <option value="15004">15004</option>
                    <option value="15005">15005</option>
                    <option value="15006">15006</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Deposit turnover Controls
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Withdrawal Fee */}
                <div>
                  <label className="text-sm text-gray-500">
                    deposit turnover set (x2)
                  </label>
                  <input
                    type="number"
                    name="deposit_turnover"
                    value={settings.deposit_turnover}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    placeholder="e.g. 2"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Marquee Announcement
              </h2>

              <div>
                <label className="text-sm text-gray-500">
                  Marquee Message (Displays scrolling text on user dashboard)
                </label>
                <textarea
                  name="marquee_msg"
                  value={settings.marquee_msg}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900"
                  placeholder="Enter message to display on the user dashboard..."
                />
              </div>
            </div>

            <div className="bg-white border rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Homepage Banners
              </h2>

              <div className="space-y-4">
                {/* Banner list */}
                {settings.banners && settings.banners.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {settings.banners.map((banner, index) => (
                      <div key={index} className="relative group border rounded-xl overflow-hidden aspect-[2.5] bg-gray-100 flex items-center justify-center">
                        <img
                          src={`${api.defaults.baseURL ? api.defaults.baseURL.replace(/\/admin\/?$/, "") : "https://api.jeetoo.bet"}/uploads/${banner}`}
                          alt={`Banner ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleBannerDelete(index)}
                          className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-md transition-opacity duration-200"
                          title="Delete Banner"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 py-4 text-center border-2 border-dashed rounded-xl">
                    No custom banners uploaded. The default system banners are currently shown on the frontend dashboard.
                  </div>
                )}

                {/* Upload Section */}
                <div className="pt-2">
                  <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50">
                    <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    {uploadingBanner ? "Uploading..." : "Upload New Banner"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      disabled={uploadingBanner}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Recommended resolution: 1920x800 or similar landscape aspect ratio. Max size 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* ================= ADMIN CONTROLS ================= */}
            <div className="bg-white border rounded-2xl p-6 shadow-sm hidden">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Admin Controls
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">
                    Minimum Deposit Threshold
                  </label>
                  <input
                    type="number"
                    name="min_deposit"
                    value={settings.min_deposit}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    placeholder="e.g. 100"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-500">
                    Minimum Valid Bets
                  </label>
                  <input
                    type="number"
                    name="min_valid_bets"
                    value={settings.min_valid_bets}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    placeholder="e.g. 5"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-500">
                    Time Window (Days)
                  </label>
                  <input
                    type="number"
                    name="time_window"
                    value={settings.time_window}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1"
                    placeholder="e.g. 7"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ================= SUPPORT SETTINGS ================= */}
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Support & Contact
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500">
                Telegram Support Link
              </label>
              <input
                type="text"
                name="telegram_link"
                value={settings.telegram_link}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1"
                placeholder="https://t.me/yourchannel"
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">WhatsApp Number</label>
              <input
                type="text"
                name="whatsapp_no"
                value={settings.whatsapp_no}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1"
                placeholder="+92XXXXXXXXXX"
              />
            </div>
          </div>
        </div>

        {/* ================= FOOTER CONFIGURATION ================= */}
        {isAdmin && (
          <div className="bg-white border rounded-2xl p-6 shadow-sm space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Footer Configuration
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Manage your public website's footer links, branding, sponsors, and about us section
              </p>
            </div>

            {/* General Branding */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">General Branding & Copy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Footer Tagline</label>
                  <input
                    type="text"
                    name="footer_tagline"
                    value={settings.footer_tagline || ""}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900 text-sm"
                    placeholder="e.g. Win Like A King"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Copyright Text</label>
                  <input
                    type="text"
                    name="footer_copyright"
                    value={settings.footer_copyright || ""}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900 text-sm"
                    placeholder="e.g. © 2025 Jeetoo Copyrights. All Rights Reserved"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 font-medium">About Us Content</label>
                  <textarea
                    name="footer_about_us"
                    value={settings.footer_about_us || ""}
                    onChange={handleChange}
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900 text-sm"
                    placeholder="Enter About Us description. Use paragraph tags or newlines to separate sections."
                  />
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Social Media Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {["facebook", "instagram", "twitter", "pinterest", "youtube", "telegram"].map((social) => (
                  <div key={social}>
                    <label className="text-xs text-gray-500 font-medium capitalize">{social} Link</label>
                    <input
                      type="text"
                      value={settings.footer_social_links?.[social] || ""}
                      onChange={(e) => handleSocialChange(social, e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900 text-sm"
                      placeholder={`https://${social}.com/yourprofile`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Sponsors */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Sponsorships</h3>

              {/* Existing Sponsors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {settings.footer_sponsors && settings.footer_sponsors.length > 0 ? (
                  settings.footer_sponsors.map((sponsor) => (
                    <div key={sponsor.id} className="flex items-center gap-3 border rounded-xl p-3 bg-gray-50 relative group">
                      <img
                        src={getImageUrl(sponsor.img)}
                        alt={sponsor.name}
                        className="w-12 h-12 object-contain bg-white rounded-lg border p-1"
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-xs font-semibold text-gray-800 truncate">{sponsor.name}</span>
                        <span className="text-[10px] text-gray-500 truncate">{sponsor.role}</span>
                        <span className="text-[9px] text-gray-400 font-medium">{sponsor.year}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteSponsor(sponsor.id)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove Sponsor"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="sm:col-span-2 md:col-span-3 text-center py-4 text-xs text-gray-400 border border-dashed rounded-xl">
                    No custom sponsors configured. Currently displaying default sponsors.
                  </div>
                )}
              </div>

              {/* Add Sponsor Form */}
              <div className="bg-gray-50/50 border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Sponsor Logo</label>
                  <label className="flex items-center justify-center w-full h-9 border border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 text-xs text-gray-600">
                    {uploadingSponsorImg ? "Uploading..." : newSponsor.img ? "Change Logo" : "Upload Logo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSponsorImgUpload}
                      disabled={uploadingSponsorImg}
                      className="hidden"
                    />
                  </label>
                  {newSponsor.img && (
                    <div className="text-[9px] text-green-600 truncate mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      {newSponsor.img}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-medium">Sponsor Name</label>
                  <input
                    type="text"
                    value={newSponsor.name}
                    onChange={(e) => setNewSponsor((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                    placeholder="e.g. Wasim Akram"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-medium">Role/Sponsorship Type</label>
                  <input
                    type="text"
                    value={newSponsor.role}
                    onChange={(e) => setNewSponsor((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                    placeholder="e.g. Title Sponsor"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] text-gray-500 font-medium">Year</label>
                    <input
                      type="text"
                      value={newSponsor.year}
                      onChange={(e) => setNewSponsor((prev) => ({ ...prev, year: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                      placeholder="e.g. 2025"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addSponsor}
                    className="h-9 px-4 rounded-lg bg-gray-950 hover:bg-gray-800 text-white font-medium text-xs flex items-center justify-center whitespace-nowrap self-end"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Ambassadors */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Brand Ambassadors</h3>

              {/* Existing Ambassadors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {settings.footer_ambassadors && settings.footer_ambassadors.length > 0 ? (
                  settings.footer_ambassadors.map((ambassador) => (
                    <div key={ambassador.id} className="flex items-center gap-3 border rounded-xl p-3 bg-gray-50 relative group">
                      <img
                        src={getImageUrl(ambassador.img)}
                        alt={ambassador.name}
                        className="w-12 h-12 object-contain bg-white rounded-lg border p-1"
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-xs font-semibold text-gray-800 truncate">{ambassador.name}</span>
                        <span className="text-[9px] text-gray-400 font-medium">{ambassador.year}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteAmbassador(ambassador.id)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove Ambassador"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="sm:col-span-2 md:col-span-3 text-center py-4 text-xs text-gray-400 border border-dashed rounded-xl">
                    No custom ambassadors configured. Currently displaying default ambassadors.
                  </div>
                )}
              </div>

              {/* Add Ambassador Form */}
              <div className="bg-gray-50/50 border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Ambassador Photo/Signature</label>
                  <label className="flex items-center justify-center w-full h-9 border border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 text-xs text-gray-600">
                    {uploadingAmbassadorImg ? "Uploading..." : newAmbassador.img ? "Change Photo" : "Upload Photo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAmbassadorImgUpload}
                      disabled={uploadingAmbassadorImg}
                      className="hidden"
                    />
                  </label>
                  {newAmbassador.img && (
                    <div className="text-[9px] text-green-600 truncate mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      {newAmbassador.img}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-medium">Ambassador Name</label>
                  <input
                    type="text"
                    value={newAmbassador.name}
                    onChange={(e) => setNewAmbassador((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                    placeholder="e.g. Babar Azam"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] text-gray-500 font-medium">Year</label>
                    <input
                      type="text"
                      value={newAmbassador.year}
                      onChange={(e) => setNewAmbassador((prev) => ({ ...prev, year: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                      placeholder="e.g. 2024-2026"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addAmbassador}
                    className="h-9 px-4 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium text-xs flex items-center justify-center whitespace-nowrap self-end"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Methods Icons</h3>

              {/* Existing Payment Methods */}
              <div className="flex flex-wrap gap-3 mb-4">
                {settings.footer_payment_methods && settings.footer_payment_methods.length > 0 ? (
                  settings.footer_payment_methods.map((method, index) => (
                    <div key={index} className="flex items-center gap-2 border rounded-xl px-3 py-2 bg-gray-50 relative group">
                      <img
                        src={getImageUrl(method.img)}
                        alt={method.name}
                        className="h-6 object-contain"
                      />
                      <span className="text-[10px] font-semibold text-gray-700">{method.name}</span>
                      <button
                        type="button"
                        onClick={() => deletePaymentMethod(index)}
                        className="text-red-500 hover:text-red-700 p-0.5 ml-1"
                        title="Remove logo"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="w-full text-center py-4 text-xs text-gray-400 border border-dashed rounded-xl">
                    No custom payment methods configured. Displaying defaults (bKash, Nagad, Rocket).
                  </div>
                )}
              </div>

              {/* Add Payment Method Form */}
              <div className="bg-gray-50/50 border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end max-w-2xl">
                <div>
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Logo Icon</label>
                  <label className="flex items-center justify-center w-full h-9 border border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 text-xs text-gray-600">
                    {uploadingPaymentImg ? "Uploading..." : newPaymentMethod.img ? "Change Logo" : "Upload Logo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePaymentImgUpload}
                      disabled={uploadingPaymentImg}
                      className="hidden"
                    />
                  </label>
                  {newPaymentMethod.img && (
                    <div className="text-[9px] text-green-600 truncate mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      {newPaymentMethod.img}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-medium">Method Name</label>
                  <input
                    type="text"
                    value={newPaymentMethod.name}
                    onChange={(e) => setNewPaymentMethod((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 mt-1 bg-white text-xs"
                    placeholder="e.g. Visa"
                  />
                </div>
                <button
                  type="button"
                  onClick={addPaymentMethod}
                  className="h-9 px-4 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium text-xs flex items-center justify-center whitespace-nowrap self-end"
                >
                  Add Method
                </button>
              </div>
            </div>

            {/* Responsible Gaming & License */}
            <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Responsible Gaming */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Responsible Gaming</h3>
                <div className="flex flex-wrap gap-3 mb-4">
                  {settings.footer_responsible_gaming && settings.footer_responsible_gaming.length > 0 ? (
                    settings.footer_responsible_gaming.map((img, index) => (
                      <div key={index} className="relative group border rounded-xl p-2 bg-gray-50 flex items-center justify-center">
                        <img
                          src={getImageUrl(img)}
                          alt="Responsible gaming logo"
                          className="h-6 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => deleteResponsibleGaming(index)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow-md"
                          title="Delete Logo"
                        >
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="w-full text-center py-4 text-xs text-gray-400 border border-dashed rounded-xl">
                      No custom responsible gaming logos configured.
                    </div>
                  )}
                </div>

                <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                  </svg>
                  {uploadingResponsibleImg ? "Uploading..." : "Upload Logo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleResponsibleImgUpload}
                    disabled={uploadingResponsibleImg}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Gaming License */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Gaming License</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    {settings.footer_gaming_license && (typeof settings.footer_gaming_license === 'string' ? settings.footer_gaming_license : (settings.footer_gaming_license as any).img) ? (
                      <div className="border rounded-xl p-2 bg-gray-50 flex items-center justify-center w-36 h-16">
                        <img
                          src={getImageUrl(typeof settings.footer_gaming_license === 'string' ? settings.footer_gaming_license : (settings.footer_gaming_license as any).img)}
                          alt="License logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-36 h-16 text-center flex items-center justify-center text-xs text-gray-400 border border-dashed rounded-xl">
                        No License Image
                      </div>
                    )}

                    <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50">
                      {uploadingLicenseImg ? "Uploading..." : "Upload License Logo"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLicenseImgUpload}
                        disabled={uploadingLicenseImg}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">License Verification URL (Link)</label>
                    <input
                      type="text"
                      value={typeof settings.footer_gaming_license === 'object' ? (settings.footer_gaming_license as any)?.link || "" : ""}
                      onChange={handleLicenseLinkChange}
                      className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-1 focus:ring-gray-900 text-sm"
                      placeholder="https://licensing.authority.com/verify?id=..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= SAVE BUTTON ================= */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
